import type { Result } from "neverthrow";
import type { ZodType } from "zod";
import z from "zod";

import { mustParse, type InvalidError } from "@thalamos/common";

import { hasInvolvementEnded } from "../rules/hasInvolvementEnded.js";
import { isInvolvementLive } from "../rules/isInvolvementLive.js";
import { personActionsForThalamosAdmin } from "../rules/personActionsForThalamosAdmin.js";
import { personStateOf } from "../rules/personStateOf.js";
import {
  GuestOnCaseSchema,
  type GuestOnCase,
  type GuestOnCaseUnbranded,
} from "./guests_on_case.js";
import {
  PersonInvolvementSchema,
  type PersonInvolvement,
  type PersonInvolvementUnbranded,
} from "./involvement.js";
import {
  OrganisationIdSchema,
  type OrganisationIdUnbranded,
} from "./organisation.js";
import {
  PersonIdSchema,
  type Person,
  type PersonIdUnbranded,
} from "./person.js";
import { PersonActionSchema, type PersonAction } from "./personActions.js";
import { PersonStateSchema, type PersonState } from "./personState.js";

/**
 * A young person's record as a Thalamos admin sees it: how it is handled, with
 * the child's identity withheld. What a Thalamos admin may be sent about a
 * record is decided here once.
 *
 * Only involvements still part of the case are carried, each with the day it
 * ends where one is set: who ended one, and why, is history a Thalamos admin
 * has no need of. Its guests are those not yet ended, one past its end date
 * included until that expiry is recorded as an ending; see
 * `docs/decisions/2026-10-31-a-guests-access-ends-by-an-authored-ending-or-a-state-of-its-link.md`.
 */
export type PersonWithIdentityWithheldUnbranded = {
  personId: PersonIdUnbranded;
  state: PersonState;
  openedBy: { organisationId: OrganisationIdUnbranded; name: string }[];
  involvements: PersonInvolvementUnbranded[];
  guests: GuestOnCaseUnbranded[];
  actions: PersonAction[];
};

export const PersonWithIdentityWithheldSchema = z
  .object({
    personId: PersonIdSchema,
    state: PersonStateSchema,
    openedBy: z.array(
      z.object({ organisationId: OrganisationIdSchema, name: z.string() }),
    ),
    involvements: z.array(PersonInvolvementSchema),
    guests: z.array(GuestOnCaseSchema),
    actions: z.array(PersonActionSchema),
  })
  .brand<"haven-common:PersonWithIdentityWithheld">() satisfies ZodType<PersonWithIdentityWithheldUnbranded>;

export type PersonWithIdentityWithheld = z.infer<
  typeof PersonWithIdentityWithheldSchema
>;

/** Parsed rather than assembled, so nothing a spread carried beyond the schema is sent. */
export const personWithIdentityWithheldOf = ({
  person,
  involvements,
  guests,
  now = new Date(),
}: {
  person: Readonly<Pick<Person, "id" | "archivedAt">>;
  involvements: readonly PersonInvolvement[];
  guests: readonly GuestOnCase[];
  now?: Date;
}): Result<PersonWithIdentityWithheld, InvalidError> =>
  mustParse(
    PersonWithIdentityWithheldSchema,
    "A record's view with its identity withheld is not valid",
  )({
    personId: person.id,
    state: personStateOf(person, involvements, now),
    openedBy: involvements.flatMap(({ kind, party }) =>
      kind === "created_by" && party.kind === "organisation"
        ? [{ organisationId: party.organisationId, name: party.name }]
        : [],
    ),
    involvements: involvements
      .filter((involvement) => isInvolvementLive(involvement, now))
      .map((involvement) => ({
        ...involvement,
        endedCause: null,
        endedByUserName: null,
      })),
    guests: guests.filter((guest) => !hasInvolvementEnded(guest, now)),
    actions: personActionsForThalamosAdmin({ involvements, now }),
  });
